import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import type { HumanResourceColumn } from "hooks/useEmailsRestApi";
import type React from "react";
import type { FC } from "react";

type ParsedHRViewProps = {
  hrColumn: HumanResourceColumn;
  onRegisterAsTalent?: () => void;
  isRegistering?: boolean;
};

// 単価をフォーマット（万円単位で表示）
const formatReward = (reward: string | null | undefined): string => {
  if (!reward || reward === "null") return "-";
  const num = parseInt(reward, 10);
  if (Number.isNaN(num)) return reward;
  // 値が1000以上の場合は万円単位に変換（例: 500000 → 50万円）
  if (num >= 1000) {
    return `${Math.round(num / 10000)}万円`;
  }
  return `${num}万円`;
};

// 業界カテゴリを日本語に変換
const formatIndustryCategory = (
  category: string | null | undefined,
): string => {
  if (!category) return "-";
  switch (category) {
    case "finance":
      return "金融";
    case "insurance":
      return "保険";
    case "government":
      return "官公庁";
    case "manufacturing":
      return "製造業";
    case "healthcare":
      return "医療";
    case "education":
      return "教育";
    case "retail":
      return "小売";
    case "startup":
      return "スタートアップ";
    default:
      return category;
  }
};

// 性別をフォーマット
const formatGender = (gender: string | null | undefined): string => {
  if (!gender) return "-";
  switch (gender) {
    case "male":
      return "男性";
    case "female":
      return "女性";
    default:
      return gender;
  }
};

// 人材種別をフォーマット
const formatHrTypes = (hrTypes: string | null | undefined): string => {
  if (!hrTypes) return "-";
  switch (hrTypes) {
    case "fullTimeEmployee":
      return "正社員";
    case "contractEmployee":
      return "契約社員";
    case "freelancer":
      return "フリーランス";
    default:
      return hrTypes;
  }
};

// リモート頻度をフォーマット
const formatRemoteFrequency = (
  frequency: string | null | undefined,
): string => {
  if (!frequency) return "-";
  switch (frequency) {
    case "full_remote":
      return "フルリモート";
    case "hybrid":
      return "ハイブリッド";
    case "onsite_only":
      return "出社のみ";
    default:
      return frequency;
  }
};

// 文字列をフォーマット
const formatString = (
  value: string | null | undefined,
  suffix = "",
): string => {
  if (value === null || value === undefined || value === "" || value === "null")
    return "-";
  return value + suffix;
};

// Boolean値をフォーマット
const formatBoolean = (
  value: boolean | null | undefined,
  trueLabel = "はい",
  falseLabel = "いいえ",
): string => {
  if (value === null || value === undefined) return "-";
  return value ? trueLabel : falseLabel;
};

// 値が空かどうかをチェック
const isEmpty = (value: string): boolean => value === "-";

// 行コンポーネント
type InfoRowProps = {
  label: string;
  value: string;
  isHighlight?: boolean;
};

const InfoRow: FC<InfoRowProps> = ({ label, value, isHighlight = false }) => {
  const isEmptyValue = isEmpty(value);
  return (
    <div
      className={`flex border-b border-gray-100 py-2 ${isEmptyValue ? "text-gray-400" : ""}`}
    >
      <div
        className={`w-32 text-sm font-medium ${isEmptyValue ? "text-gray-400" : "text-gray-500"}`}
      >
        {label}
      </div>
      <div
        className={`flex-1 text-sm ${isEmptyValue ? "text-gray-400" : isHighlight ? "text-blue-600 font-medium" : "text-gray-900"}`}
      >
        {value}
      </div>
    </div>
  );
};

// Booleanバッジ行コンポーネント
type BooleanRowProps = {
  label: string;
  value: string;
  isTrue: boolean | null | undefined;
};

const BooleanRow: FC<BooleanRowProps> = ({ label, value, isTrue }) => {
  const isEmptyValue = isEmpty(value);
  return (
    <div
      className={`flex border-b border-gray-100 py-2 ${isEmptyValue ? "text-gray-400" : ""}`}
    >
      <div
        className={`w-32 text-sm font-medium ${isEmptyValue ? "text-gray-400" : "text-gray-500"}`}
      >
        {label}
      </div>
      <div className="flex-1 text-sm">
        {isEmptyValue ? (
          <span className="text-gray-400">{value}</span>
        ) : (
          <span
            className={`px-2 py-0.5 rounded text-xs ${
              isTrue
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {value}
          </span>
        )}
      </div>
    </div>
  );
};

// タグリスト行コンポーネント
type TagListRowProps = {
  label: string;
  values: string[] | null | undefined;
  colorClass?: string;
};

const TagListRow: FC<TagListRowProps> = ({
  label,
  values,
  colorClass = "bg-blue-100 text-blue-800",
}) => {
  const hasValues = values && values.length > 0;
  return (
    <div
      className={`flex border-b border-gray-100 py-2 ${!hasValues ? "text-gray-400" : ""}`}
    >
      <div
        className={`w-32 text-sm font-medium flex-shrink-0 ${!hasValues ? "text-gray-400" : "text-gray-500"}`}
      >
        {label}
      </div>
      <div className="flex-1 flex flex-wrap gap-1">
        {hasValues ? (
          values.map(value => (
            <span
              key={value}
              className={`px-2 py-0.5 rounded text-xs ${colorClass}`}
            >
              {value}
            </span>
          ))
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </div>
    </div>
  );
};

// セクションコンポーネント
type SectionProps = {
  title: string;
  children: React.ReactNode;
};

const Section: FC<SectionProps> = ({ title, children }) => {
  return (
    <div className="mb-6">
      <h3 className="text-base font-semibold text-gray-800 mb-3 pb-2 border-b-2 border-gray-200">
        {title}
      </h3>
      <div>{children}</div>
    </div>
  );
};

export const ParsedHRView: FC<ParsedHRViewProps> = ({
  hrColumn,
  onRegisterAsTalent,
  isRegistering,
}) => {
  // フォーマット済みの値を準備
  const age = formatString(hrColumn.age, "歳");
  const gender = formatGender(hrColumn.gender);
  const hrTypes = formatHrTypes(hrColumn.hrTypes);
  const station = formatString(hrColumn.station);
  const startedDate = formatString(hrColumn.startedDate);
  const reward = formatReward(hrColumn.reward);
  const industryCategory = formatIndustryCategory(hrColumn.industryCategory);
  const remoteFrequency = formatRemoteFrequency(hrColumn.remoteFrequency);
  const isRemoteNeeded = formatBoolean(hrColumn.isRemoteNeeded, "あり", "なし");
  const isJapanese = formatBoolean(hrColumn.isJapanese, "はい", "いいえ");
  const isDirect = formatBoolean(hrColumn.isDirect, "はい", "いいえ");
  const depthLabel =
    hrColumn.depth != null
      ? hrColumn.depth === 0
        ? "自社所属"
        : hrColumn.depth === 1
          ? "直所属"
          : `${hrColumn.depth - 1}社先`
      : "-";
  const isMultiple = formatBoolean(hrColumn.isMultiple, "複数", "単独");

  return (
    <div className="space-y-4">
      {/* CRM連携ボタン */}
      {onRegisterAsTalent && (
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            onClick={onRegisterAsTalent}
            disabled={isRegistering}
          >
            {isRegistering ? <Loading size="xs" /> : "タレントとして登録"}
          </Button>
        </div>
      )}

      {/* 人材情報 */}
      <Section title="人材情報">
        <InfoRow label="年齢" value={age} />
        <InfoRow label="性別" value={gender} />
        <InfoRow label="人材種別" value={hrTypes} />
        <InfoRow label="最寄駅" value={station} />
        <InfoRow label="稼働開始" value={startedDate} />
        <InfoRow
          label="希望単価"
          value={reward}
          isHighlight={!isEmpty(reward)}
        />
        <InfoRow label="業界" value={industryCategory} />
      </Section>

      {/* 勤務条件 */}
      <Section title="勤務条件">
        <BooleanRow
          label="リモート希望"
          value={isRemoteNeeded}
          isTrue={hrColumn.isRemoteNeeded}
        />
        <InfoRow label="リモート頻度" value={remoteFrequency} />
        <BooleanRow
          label="日本国籍"
          value={isJapanese}
          isTrue={hrColumn.isJapanese}
        />
        <BooleanRow label="直属" value={isDirect} isTrue={hrColumn.isDirect} />
        <InfoRow label="商流" value={depthLabel} />
        <BooleanRow
          label="複数候補者"
          value={isMultiple}
          isTrue={hrColumn.isMultiple}
        />
      </Section>

      {/* 技術スキル */}
      <Section title="技術スキル">
        <TagListRow
          label="技術"
          values={hrColumn.technology}
          colorClass="bg-blue-100 text-blue-800"
        />
      </Section>
    </div>
  );
};
